import type { GameState, Lane, Mob, Projectile, Tower } from "@mmotdg/shared";
import * as THREE from "three";
import { LaneTransform } from "./LaneTransform.js";
import { MapRenderer } from "./MapRenderer.js";
import { MobRenderer } from "./MobRenderer.js";
import { ProjectileRenderer } from "./ProjectileRenderer.js";
import { TowerRenderer } from "./TowerRenderer.js";

// Per-lane wrapper used in boss modes. Holds one set of sub-renderers whose
// objects live in a single Three.js group, which the GameRenderer positions
// radially around the hub.
export class LaneScene {
  readonly group = new THREE.Group();
  readonly mapRenderer: MapRenderer;
  readonly mobRenderer: MobRenderer;
  readonly towerRenderer: TowerRenderer;
  readonly projectileRenderer: ProjectileRenderer;
  readonly laneId: string;
  readonly yMin: number;
  readonly yMax: number;
  readonly groundPlane: THREE.Plane;
  readonly transform: LaneTransform;

  constructor(
    scene: THREE.Scene,
    state: GameState,
    lane: Lane,
    seat: number,
    totalSeats: number,
    hubRadius: number,
    outerRadius: number,
  ) {
    this.laneId = lane.id;
    const laneRow = lane.spawnCell.cy;
    // 11-row bands (BOSS_BAND_ROWS) centred on laneRow. yMin/yMax cover the
    // whole band for the slice filter.
    this.yMin = Math.max(0, laneRow - 5);
    this.yMax = Math.min(state.gridHeight - 1, laneRow + 5);

    // Build the polar transform for this seat. seat 0 lives at +Z (south,
    // active player position); others fan around the hub from there. The
    // slice fraction makes wedges narrower than 360°/N so there's visible
    // empty space between players around the disk.
    // SLICE_FRACTION = how much of each seat's angular budget the playable
    // wedge consumes; the rest is the gap between adjacent players. Linear
    // from 2p → 0.5 (half-budget, huge gaps for clarity in sparse games) to
    // 6p → 1.0 (no gap, slices fully touch so every cell is reachable).
    // 2p → 0.50, 3p → 0.625, 4p → 0.75, 5p → 0.875, 6p → 1.00.
    const SLICE_FRACTION = Math.min(1.0, 0.5 + Math.max(0, totalSeats - 2) * 0.125);
    const sliceSpan = (Math.PI * 2 / Math.max(1, totalSeats)) * SLICE_FRACTION;
    const sliceCenter = (seat / totalSeats) * Math.PI * 2 + Math.PI / 2;
    const bandHalf = (this.yMax - this.yMin) / 2;
    this.transform = new LaneTransform(
      sliceCenter,
      sliceSpan,
      hubRadius,
      outerRadius,
      laneRow,
      bandHalf,
      lane.spawnCell.cx,
      lane.baseCell.cx,
    );

    scene.add(this.group);

    this.mapRenderer = new MapRenderer(this.group, state, {
      slice: { laneId: lane.id, yMin: this.yMin, yMax: this.yMax },
      transform: this.transform,
    });
    this.mobRenderer = new MobRenderer(this.group);
    this.mobRenderer.setTransform(this.transform);
    this.towerRenderer = new TowerRenderer(this.group);
    this.towerRenderer.setTransform(this.transform);
    this.projectileRenderer = new ProjectileRenderer(this.group);
    this.projectileRenderer.setTransform(this.transform);

    this.groundPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
  }

  setAlive(alive: boolean): void {
    this.mapRenderer.setSliceAlive(alive);
    this.towerRenderer.setSliceAlive(alive);
  }

  // Filter entities so only those belonging to this lane go through.
  filterMobs(mobs: Mob[]): Mob[] {
    return mobs.filter((m) => m.laneId === this.laneId);
  }

  filterTowers(towers: Tower[], ownerByLane: Map<string, string>): Tower[] {
    return towers.filter((t) => ownerByLane.get(this.laneId) === t.ownerId);
  }

  filterProjectiles(projectiles: Projectile[], towerLane: Map<string, string>): Projectile[] {
    return projectiles.filter((p) => towerLane.get(p.towerId) === this.laneId);
  }

  // Position and rotate the lane around the central hub. seat = lane index,
  // total = number of lanes. Lane is oriented so its spawn cell sits just
  // outside the hub and the base cell points outward — mobs spawn near the
  // boss and walk outward toward the player's base.
  // Convert a world ray hit into flat grid coords for this lane, returning
  // null if the hit isn't inside this slice's annulus.
  worldToCell(worldHit: THREE.Vector3): { cx: number; cy: number } | null {
    const hit = this.transform.worldToFlat(worldHit.x, worldHit.z);
    if (!hit) return null;
    if (hit.cy < this.yMin || hit.cy > this.yMax) return null;
    return hit;
  }

  dispose(): void {
    this.projectileRenderer.dispose();
    this.towerRenderer.dispose();
    this.mobRenderer.dispose();
    this.mapRenderer.dispose();
    this.group.parent?.remove(this.group);
  }
}
